import LZUTF8 from 'lzutf8';
import { USER_DATA_STORAGE_KEY } from '../constants';
import type { UserState, PayloadData, UParamData, HolidayPromoByFlow, HolidayPromoFlow } from '../types';

function readHolidayPromo(value: unknown): HolidayPromoByFlow {
    if (!value || typeof value !== 'object') return {};

    const result: HolidayPromoByFlow = {};
    for (const flow of ['hp', 'hp-3', 'hp-23d'] as HolidayPromoFlow[]) {
        const entry = (value as Record<string, unknown>)[flow];
        if (!entry || typeof entry !== 'object') continue;

        const { product_scope, declined } = entry as Record<string, unknown>;
        if ((product_scope === 'discount' || product_scope === 'extra_discount') && typeof declined === 'boolean') {
            result[flow] = { product_scope, declined };
        }
    }
    return result;
}

/**
 * Parse data from the "u" parameter from the URL
 */
export function parseUParam(decodeFromUrl: (url?: string | null) => any): UParamData | null {
    try {
        const data = decodeFromUrl();
        if (!data || typeof data !== 'object') {
            return null;
        }

        return data as UParamData;
    } catch (error) {
        console.error('[Storage] Error parsing u param:', error);
        return null;
    }
}

/**
 * Parse the payload parameter from the URL
 */
export function parsePayload(): PayloadData | null {
    try {
        const raw = window.location.search.split("payload=")[1];
        if (!raw) {

            return null;
        }

        const encodedPayload = raw.includes("&") ? raw.split("&")[0] : raw;
        const payload = decodeURIComponent(encodedPayload);

        const decompressed = LZUTF8.decompress(payload, {
            inputEncoding: "Base64",
            outputEncoding: "String",
        });

        if (!decompressed || !decompressed.includes("~")) {
            console.warn('[Storage] Invalid decompressed data format - missing separator');
            return null;
        }

        const [userName = "", userEmail = ""] = decompressed.split("~");

        const trimmedUserName = userName?.trim() || "";
        const trimmedUserEmail = userEmail?.trim() || "";

        if (!trimmedUserName && !trimmedUserEmail) {
            return null;
        }

        return {
            userName: trimmedUserName || undefined,
            userEmail: trimmedUserEmail || undefined
        };
    } catch (error) {
        console.error('[Storage] Payload decompression error:', error);
        return null;
    }
}

/**
 * Reads data from localStorage
 */
export function readFromLocalStorage(): Partial<UserState> | null {
    try {
        const storedData = localStorage.getItem(USER_DATA_STORAGE_KEY);
        if (!storedData) {
            return null;
        }

        const parsedData = JSON.parse(storedData);
        return parsedData;
    } catch (error) {
        console.error('[Storage] Error reading from localStorage:', error);
        return null;
    }
}

/**
 * Synchronizes state with u parameter and localStorage
 */
export function syncToStorage(
    state: UserState,
    encodeToUrl: (data: any, replaceCurrentUrl?: boolean) => string | null
): void {
    try {
        const nonNullData = Object.fromEntries(
            Object.entries(state).filter(([_, value]) => value !== null)
        );

        if (Object.keys(nonNullData).length === 0) {
            return;
        }

        try {
            encodeToUrl(nonNullData, true);
        } catch (error) {
            console.error('[Storage] Error encoding to URL:', error);
        }

        try {
            localStorage.setItem(USER_DATA_STORAGE_KEY, JSON.stringify(nonNullData));
        } catch (error) {
            console.error('[Storage] Error writing to localStorage:', error);
        }
    } catch (error) {
        console.error('[Storage] Error in syncToStorage:', error);
    }
}

/**
 * Merge data from different sources by priority
 */
export function mergeDataSources(
    uParamData: UParamData | null,
    payloadData: PayloadData | null,
    localStorageData: Partial<UserState> | null
): Partial<UserState> {
    const merged: Partial<UserState> = {};

    if (localStorageData) {
        Object.assign(merged, localStorageData);
    }

    if (payloadData) {
        if (payloadData.userName) merged.name = payloadData.userName;
        if (payloadData.userEmail) merged.email = payloadData.userEmail;
    }

    if (uParamData) {
        Object.assign(merged, uParamData);
    }

    // A link for another customer must not inherit this browser's offer choices.
    const differentUser = Boolean(uParamData?.cid && uParamData.cid !== localStorageData?.cid);
    const holidayPromo = {
        ...(!differentUser ? readHolidayPromo(localStorageData?.holiday_promo) : {}),
        ...readHolidayPromo(uParamData?.holiday_promo),
    };
    delete merged.holiday_promo;
    if (Object.keys(holidayPromo).length > 0) {
        merged.holiday_promo = holidayPromo;
    }

    return merged;
}
